import { Switch as SwitchPrimitive } from 'radix-ui';
import { useId } from 'react';

import { PillBadge } from '@/components/custom/pill-badge';
import { cn } from '@/lib/utils';

/**
 * The two canonical ways to render billable state. Everything that shows
 * whether time is billable goes through one of them, so a tweak to the
 * copy or the palette lands everywhere at once:
 *
 *   - {@link BillableBadge}  — read-only, for lists and detail views.
 *   - {@link BillableSwitch} — editable (or read-only when `onToggle` is
 *     omitted), for entry forms.
 *
 * Don't hand-roll a third variant; extend one of these instead.
 */

/**
 * Read-only Billable / Non-billable badge. Used by the approvals tree, the
 * approved-search results, and the locked-cell read-only card.
 *
 * Built on `PillBadge`, not the shadcn `Badge` component — `Badge`'s
 * `outline` variant sets `data-variant="outline"`, the same attribute the
 * Numera outlined-control rules in `globals.css` key off of for buttons, so
 * this read-only pill was inheriting a button's hover fill and focus glow.
 * Billable reuses the exact "Active" green from `StatusBadge` (customer
 * Active/Inactive); non-billable reuses its exact "Inactive" muted
 * treatment, so the two badge families read as one shape, one language.
 */
export function BillableBadge({ billable, className }: { billable: boolean; className?: string }) {
  return (
    <PillBadge
      variant="outline"
      className={cn(
        billable
          ? 'border-success/30 bg-success/10 text-success mt-0.5'
          : 'border-border bg-transparent text-muted-foreground mt-0.5',
        className,
      )}
    >
      {billable ? 'Billable' : 'Non-billable'}
    </PillBadge>
  );
}

/**
 * Billable on/off switch used in the day-list row cards. A labeled toggle
 * reads as an obvious control — unlike the old clickable pill, where it was
 * unclear the badge was interactive. The track turns emerald when on, and
 * the adjacent label spells out the current state. When `onToggle` is
 * omitted the switch is read-only.
 */
export function BillableSwitch({
  billable,
  disabled,
  onToggle,
}: {
  billable: boolean;
  disabled?: boolean;
  onToggle?: () => void;
}) {
  const interactive = Boolean(onToggle) && !disabled;
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-wide transition-colors',
        interactive ? 'cursor-pointer' : 'cursor-default',
        billable ? 'text-emerald-900 dark:text-emerald-200' : 'text-muted-foreground',
      )}
    >
      <SwitchPrimitive.Root
        id={id}
        checked={billable}
        disabled={!interactive}
        onCheckedChange={() => onToggle?.()}
        aria-label="Billable"
        className={cn(
          'peer inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors disabled:cursor-default disabled:opacity-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          billable ? 'bg-emerald-500' : 'bg-muted-foreground/30',
        )}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none block size-3 rounded-full bg-white shadow-sm transition-transform',
            billable ? 'translate-x-3.5' : 'translate-x-0.5',
          )}
        />
      </SwitchPrimitive.Root>
      {billable ? 'Billable' : 'Non-billable'}
    </label>
  );
}
